<script setup lang="ts">
import { JOB_PACK_APPLICATIONS, JOB_PACK_DAYS } from '~/constants/pricing'

/**
 * The Job Search Pack at the end of a CV's result, as the one offer that does
 * everything the steps above it do, for every posting.
 *
 * The value is stacked from our own prices, never an invented "worth $X": what
 * the visitor was just offered one by one (a tailored CV, an interview prep, a
 * clean PDF) with its price, and what the pack makes of it (unlimited, or
 * included). Under the stack, the sum the visitor would really pay for three
 * applications bought one by one, next to the pack's one payment. Every number
 * comes from the visitor's price list (usePricing), so a price change reaches
 * this too.
 *
 * The page's buttons go in the `cta` slot, the claim after a guest payment too.
 */
withDefaults(defineProps<{
  /** As the checkout charges it: less what was paid for this CV before (usePackCredit). */
  price: string
  /** About this visitor (their role, their match score) instead of the general line. */
  headline?: string
  sub?: string
  /** Paid, waiting for the account: no badge or comparison, only the claim in the slot. */
  bought?: boolean
}>(), {
  headline: undefined,
  sub: undefined,
  bought: false,
})

const { t } = useI18n()
const { prices, format } = usePricing().pricing.value

/** One application bought on its own: the CV tailored to it and the interview prep for it. */
const APPLICATIONS = 3
const oneByOne = format(APPLICATIONS * (prices.tailoredPdf + prices.interviewPrep))
const perApplication = format(prices.jobPack / JOB_PACK_APPLICATIONS)

/** What each step above costs on its own, and what the pack makes of it. */
const rows = [
  { id: 'tailor', icon: 'bi-magic', unit: format(prices.tailoredPdf), value: 'unlimited' },
  { id: 'interview', icon: 'bi-chat-quote', unit: format(prices.interviewPrep), value: 'unlimited' },
  { id: 'clean', icon: 'bi-file-earmark-check', unit: format(prices.exportCredit), value: 'included' },
  { id: 'letters', icon: 'bi-envelope-paper', unit: null, value: 'unlimited' },
  { id: 'web', icon: 'bi-globe2', unit: null, value: 'included' },
] as const
</script>

<template>
  <div class="overflow-hidden rounded-[0.625rem] border-2 border-ink bg-white dark:border-white dark:bg-gray-800">
    <div v-if="!bought" class="flex items-center justify-between gap-3 bg-ink px-5 py-2.5 text-white dark:bg-white dark:text-ink">
      <span class="text-xs font-bold uppercase tracking-wide">{{ t('landing.builder.packOffer.badge') }}</span>
      <i class="bi bi-rocket-takeoff-fill text-lg leading-none text-lilac" aria-hidden="true" />
    </div>

    <div class="p-5 md:p-7">
      <div class="text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
        {{ t('premium.jobPack.title') }}
      </div>
      <div class="mt-1 font-title text-xl font-bold leading-snug text-ink md:text-2xl dark:text-white">
        {{ headline || t('landing.builder.packOffer.headline') }}
      </div>
      <p class="mt-2 !text-sm text-body md:!text-base dark:text-gray-300">{{ sub || t('landing.builder.packOffer.sub') }}</p>

      <!-- The stack: each step's own price, and what the pack makes of it -->
      <ul class="mt-5 divide-y divide-line rounded-[0.625rem] border border-line dark:divide-gray-700 dark:border-gray-700">
        <li v-for="row in rows" :key="row.id" class="flex items-start gap-3 px-3 py-2.5 md:px-4">
          <i class="bi mt-0.5 shrink-0 text-lg leading-none text-accent" :class="row.icon" aria-hidden="true" />
          <div class="min-w-0 grow">
            <div class="font-title !text-sm font-semibold text-ink md:!text-base dark:text-white">
              {{ t(`landing.builder.packOffer.rows.${row.id}`, { days: JOB_PACK_DAYS }) }}
            </div>
            <div v-if="row.unit" class="!text-xs text-muted dark:text-gray-400">
              {{ t('landing.builder.packOffer.alone', { price: row.unit }) }}
            </div>
          </div>
          <span
            class="shrink-0 self-center rounded-full px-2 py-0.5 !text-xs font-bold"
            :class="row.value === 'unlimited' ? 'bg-accent/15 text-accent-strong dark:text-accent-soft' : 'bg-lilac/40 text-ink dark:text-white'"
          >
            {{ t(`landing.builder.packOffer.${row.value}`) }}
          </span>
        </li>
      </ul>

      <!-- One by one against one payment, as two rows of the same shape -->
      <div v-if="!bought" class="mt-4 rounded-[0.625rem] bg-surface p-4 dark:bg-gray-900">
        <div class="flex items-baseline justify-between gap-4">
          <div class="min-w-0 !text-sm text-body dark:text-gray-300">
            {{ t('landing.builder.packOffer.oneByOne', { n: APPLICATIONS }) }}
          </div>
          <!-- Not struck through: it is what buying one by one costs, never a former price of the pack -->
          <div class="shrink-0 font-title text-lg font-bold tabular-nums text-muted dark:text-gray-400">{{ oneByOne }}</div>
        </div>
        <div class="my-3 h-px w-full bg-line dark:bg-gray-700" />
        <div class="flex items-baseline justify-between gap-4">
          <div class="min-w-0">
            <div class="font-title text-base font-bold text-ink dark:text-white">{{ t('landing.builder.packOffer.packLabel') }}</div>
            <div class="!text-xs text-muted dark:text-gray-400">{{ t('landing.builder.packOffer.packNote', { days: JOB_PACK_DAYS }) }}</div>
          </div>
          <div class="shrink-0 font-title text-4xl font-bold tabular-nums text-ink dark:text-white">{{ price }}</div>
        </div>
        <p class="mt-3 !text-xs text-body dark:text-gray-300">
          {{ t('premium.jobPack.stack.perApplication', { count: JOB_PACK_APPLICATIONS, price: perApplication }) }}
        </p>
      </div>

      <div v-if="$slots.cta" class="mt-5">
        <slot name="cta" />
      </div>
    </div>
  </div>
</template>
